import React, { useEffect, useState } from "react";
import { maintenanceApi, ZitadelProvisionedOrg } from "../../../api/maintenance";
import { errText, useProjectRoles, RolePicker } from "./zitadelShared";
import { useNotice } from "../shared/useNotice";

/**
 * Flow 4 — change the Admin Console project-grant role keys for an
 * already-provisioned org.
 */

const ZitadelFlowManageGrants: React.FC = () => {
  const { roles } = useProjectRoles();
  const [orgs, setOrgs] = useState<ZitadelProvisionedOrg[]>([]);
  const [tenantId, setTenantId] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useNotice("error", "");
  const [message, setMessage] = useNotice("success", "");

  useEffect(() => {
    maintenanceApi
      .zitadelProvisionedOrgs()
      .then((r) => setOrgs(r.data))
      .catch((e) => setError(errText(e, "Failed to load provisioned orgs")));
  }, []);

  const loadGrant = async (tid: string) => {
    setTenantId(tid);
    setLoaded(false);
    setMessage("");
    setError("");
    if (!tid) return;
    setBusy(true);
    try {
      const r = await maintenanceApi.zitadelGetGrant(tid);
      setSelected(r.data.role_keys || []);
      setLoaded(true);
    } catch (e) {
      setError(errText(e, "Failed to load current grant"));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (selected.length === 0) return setError("Pick at least one role");
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const r = await maintenanceApi.zitadelUpdateGrant(tenantId, {
        role_keys: selected,
      });
      setSelected(r.data.role_keys || []);
      setMessage("Grant roles updated");
    } catch (e) {
      setError(errText(e, "Failed to update grant"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ztl-card">
      <h3 style={{ marginTop: 0 }}>Manage project grant roles</h3>
      <label className="ztl-field">
        <span>Provisioned organization *</span>
        <select
          value={tenantId}
          onChange={(e) => loadGrant(e.target.value)}
        >
          <option value="">Select organization...</option>
          {orgs.map((o) => (
            <option key={o.org_custom_id} value={o.zitadel_tenant_id}>
              {o.name}
            </option>
          ))}
        </select>
      </label>

      {message && <p className="maint-success">{message}</p>}
      {error && <p className="maint-form-error">{error}</p>}

      {loaded && (
        <>
          <span style={{ fontSize: 13, fontWeight: 600 }}>
            Role keys granted to this org
          </span>
          <RolePicker
            roles={roles}
            selected={selected}
            onChange={setSelected}
          />
          <div className="maint-form-actions" style={{ marginTop: 14 }}>
            <button
              className="maint-btn-primary"
              disabled={busy}
              onClick={save}
            >
              {busy ? "Saving..." : "Save grant roles"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ZitadelFlowManageGrants;
